import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import moment from 'moment';
import { createSelector } from 'reselect';
import { get } from 'lodash/fp';
import { StoreState } from '../store-interfaces';
import {
  EnhancedPlan,
  FormattedInvoice,
  Plan,
  PlansMapping,
  PlanSubtypes,
  PlanTypeBE,
  ResponseInvoice,
  SubscriptionResponse,
} from '../interfaces/subscription';
import { ErrorReportingUtils } from '@mm/plugins';
import ConnectedPageModel from '../model/ConnectedPageModel';
import { getSubscriptionTabs } from '../helpers/billing-subscription-tab-helpers';
import {
  enhancedBillingPagesSelector,
  getPageBillingTypeByPlan,
  selectPlansMapping,
} from './facebookPageSelector';
import { PageBillingType } from '../interfaces/connected-page';
import { selectActiveCompanyId } from './viewSelectors';
import { combinedFeatureFlagsSelector } from './featureFlagsSelectors';

const subscriptionState = (state: StoreState) => state.subscription;

const selectPlans = createSelector(subscriptionState, get('plans'));
const selectPlanInfo = createSelector(subscriptionState, get('planInfo'));

export const selectAccountDetails = createSelector(
  subscriptionState,
  get('accountDetails'),
);

export const selectCreditsForFuturePayments = createSelector(
  subscriptionState,
  get('futurePaymentCredits'),
);

const selectSubscriptions = (state: StoreState) => {
  const details = selectAccountDetails(state);
  return details === undefined ? [] : details.subscriptions || [];
};

const selectInvoices = (state: StoreState) => {
  const { accountDetails } = state.subscription;
  const invoices = accountDetails ? accountDetails.invoices : [];

  return invoices || [];
};

export const selectPriceForNextLevel = (state: StoreState) => {
  const { priceForNextLevel } = state.subscription;
  return priceForNextLevel || {};
};

const getFormattedTotalCost = (totalCost: number) => {
  if (totalCost) {
    const absoluteValue = Math.abs(totalCost);
    const fixedNumber = absoluteValue.toFixed(2);
    return Number(fixedNumber);
  }

  return 0;
};

const getFormattedInvoices = (
  invoices: ResponseInvoice[],
): FormattedInvoice[] =>
  invoices.map(invoice => {
    const createdAt = moment(invoice.createdAt).format('MM/DD/YYYY');
    const dueOn = moment(invoice.dueOn).format('MM/DD/YYYY');
    const status = invoice.status ? invoice.status : 'Unknown';
    const formattedTotalCost = getFormattedTotalCost(invoice.totalCost);

    return {
      ...invoice,
      id: invoice.invoiceNumber,
      formattedTotalCost,
      createdAt,
      dueOn,
      status,
    };
  });

const getDefaultPlanByBillingType =
  (pageBillingType: PageBillingType) =>
  (
    plans: EnhancedPlan[],
    features: ReturnType<typeof combinedFeatureFlagsSelector>,
  ): EnhancedPlan | undefined => {
    if (!plans.length) {
      return;
    }
    const { november2021Pricing } = features;

    const matchingPlans = plans.filter(
      plan =>
        plan?.billingPeriod !== 'year' &&
        plan.pageBillingType === pageBillingType,
    );

    if (matchingPlans.length) {
      if (matchingPlans.length > 1) {
        ErrorReportingUtils.reportError(
          `Multiple plans match default ${pageBillingType} plan`,
          {
            extraData: { plans },
          },
        );
      }

      // Note: for new pricing we want to get newest plans as default ones. But keep old order for old ones
      const sortedDescIdPlans = november2021Pricing
        ? matchingPlans.sort((a, b) => b.id - a.id)
        : matchingPlans;

      return sortedDescIdPlans[0];
    }

    ErrorReportingUtils.reportError(
      `No default ${pageBillingType} plan found`,
      {
        extraData: { plans },
      },
    );
  };

const getHasTeamSubscription = (
  plans: Plan[],
  accountDetails?: SubscriptionResponse,
): boolean => {
  if (accountDetails && accountDetails.subscriptions) {
    const activePlans = accountDetails.subscriptions.map(subscription =>
      plans.find(plan => subscription.planCode === plan.planCode),
    );

    return !!activePlans.find(
      plan => !!plan && plan.planType === PlanTypeBE.team,
    );
  }

  return false;
};

const getHas119PlusSubscription = (
  plans: Plan[],
  accountDetails?: SubscriptionResponse,
) => {
  if (accountDetails && accountDetails.subscriptions) {
    return !!accountDetails.subscriptions.find(s => s.basePrice > 11900);
  }

  return false;
};

export const getShouldShowPercentageBar = (
  facebookPage: ConnectedPageModel,
  plans: Plan[],
): boolean => {
  const subscription = facebookPage?.subscription;
  if (!subscription && !facebookPage.redemption) {
    return !!facebookPage.outgoingMessagesLimit;
  }

  if (subscription && isBilling2023Subscription(subscription)) {
    return false;
  }

  if (subscription && !facebookPage.redemption) {
    const { planCode } = subscription;
    const plan = plans.find(plan => planCode === plan.planCode);

    if (
      plan &&
      plan.planType !== PlanTypeBE.team &&
      plan.planType !== PlanTypeBE.teamSpecial
    ) {
      return (
        plan.outgoingMessagesLimit !== null &&
        plan.outgoingMessagesLimit !== undefined
      );
    }

    return false;
  }
  if (facebookPage.redemption && !facebookPage.subscription) {
    return facebookPage.redemption.freeTrial;
  }

  return false;
};

const getEnhancedPlans = (
  plans: Plan[],
  mapping: PlansMapping,
): EnhancedPlan[] =>
  plans.map(plan => getEnhancedPlan(plan, mapping)) as EnhancedPlan[];

const getEnhancedPlan = (
  plan: Plan | undefined,
  mapping: PlansMapping,
): EnhancedPlan | undefined => {
  if (!plan) {
    return undefined;
  }
  const pageBillingType = getPageBillingTypeByPlan(plan, mapping);
  if (plan.planSubtypes.some(subType => subType === PlanSubtypes.team2020)) {
    return {
      ...plan,
      pageBillingType: PageBillingType.NewTeam,
    };
  }
  return {
    ...plan,
    pageBillingType,
  };
};

export const getSubscription = (state: StoreState, id?: number) => {
  if (id === undefined) {
    return undefined;
  }
  const subscriptions = selectSubscriptions(state);
  return subscriptions.find(sub => sub.id === id);
};

export const getCurrentSubscription = (id?: number) =>
  createSelector(
    [subscriptionState],
    subscription => subscription?.currentSubscription?.[Number(id)],
  );

export const getPlansUserSeats = (state: StoreState) =>
  state.subscription.plansUserSeats;

export const enhancedPlansSelector = createSelector(
  [selectPlans, selectPlansMapping],
  getEnhancedPlans,
);

export const enhancedPlanInfoSelector = createSelector(
  [selectPlanInfo, selectPlansMapping],
  getEnhancedPlan,
);

export const defaultTeamPlanSelector = createSelector(
  [enhancedPlansSelector, combinedFeatureFlagsSelector],
  getDefaultPlanByBillingType(PageBillingType.NewTeam),
);

export const defaultProUnicornPlanSelector = createSelector(
  [enhancedPlansSelector, combinedFeatureFlagsSelector],
  getDefaultPlanByBillingType(PageBillingType.ProUnicorn),
);

export const defaultProPlanSelector = createSelector(
  [enhancedPlansSelector, combinedFeatureFlagsSelector],
  getDefaultPlanByBillingType(PageBillingType.Pro),
);

export const defaultInstachampPlanSelector = createSelector(
  [enhancedPlansSelector, combinedFeatureFlagsSelector],
  getDefaultPlanByBillingType(PageBillingType.Instachamp),
);

export const formattedInvoicesSelector = createSelector(
  [selectInvoices],
  getFormattedInvoices,
);

export const hasTeamSubscriptionSelector = createSelector(
  [selectPlans, selectAccountDetails],
  getHasTeamSubscription,
);

export const has119PlusSubscriptionSelector = createSelector(
  [selectPlans, selectAccountDetails],
  getHas119PlusSubscription,
);

export const subscriptionTabsSelector = createSelector(
  [
    enhancedBillingPagesSelector,
    enhancedPlansSelector,
    selectAccountDetails,
    selectActiveCompanyId,
  ],
  getSubscriptionTabs,
);
